/**
 * 한글 폰트 로더
 *
 * jsPDF에서 한글을 지원하기 위해 NanumGothic 폰트를 동적으로 로드합니다.
 * 로드된 폰트는 localStorage에 캐시되어 재사용됩니다.
 */

import jsPDF from 'jspdf';

// Noto Sans KR 폰트 URL (Google Fonts Static - 안정적)
const FONT_URLS = [
  // Google Fonts Static CDN (Noto Sans KR Regular)
  'https://fonts.gstatic.com/s/notosanskr/v36/PbyxFmXiEBPT4ITbgNA5Cgms3VYcOA-vvnIzzuoyeLTq8H4hfeE.ttf',
];

// 폰트 이름 상수
export const KOREAN_FONT_NAME = 'NotoSansKR';

// localStorage 캐시 키
const CACHE_KEY = 'notosanskr_base64';

/**
 * ArrayBuffer를 Base64 문자열로 변환
 */
function arrayBufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * 폰트를 로드하고 Base64로 변환
 * 여러 CDN을 순차적으로 시도
 */
async function fetchFontAsBase64(): Promise<string> {
  // 캐시 확인
  const cached = localStorage.getItem(CACHE_KEY);
  if (cached) {
    console.log('한글 폰트 캐시에서 로드');
    return cached;
  }

  console.log('한글 폰트 다운로드 중...');

  // 여러 CDN 순차 시도
  let lastError: Error | null = null;
  for (const fontUrl of FONT_URLS) {
    try {
      console.log(`폰트 다운로드 시도: ${fontUrl}`);
      const response = await fetch(fontUrl);
      if (!response.ok) {
        console.warn(`폰트 다운로드 실패 (${response.status}): ${fontUrl}`);
        continue;
      }

      const arrayBuffer = await response.arrayBuffer();
      const base64 = arrayBufferToBase64(arrayBuffer);

      // 캐시에 저장
      try {
        localStorage.setItem(CACHE_KEY, base64);
        console.log('한글 폰트 캐시에 저장 완료');
      } catch (e) {
        console.warn('폰트 캐시 저장 실패 (localStorage 용량 초과):', e);
      }

      return base64;
    } catch (e) {
      lastError = e as Error;
      console.warn(`폰트 다운로드 오류: ${fontUrl}`, e);
    }
  }

  throw new Error(`모든 CDN에서 폰트 다운로드 실패: ${lastError?.message || 'Unknown error'}`);
}

/**
 * jsPDF 인스턴스에 한글 폰트 등록
 *
 * @param doc jsPDF 인스턴스
 * @returns 폰트가 등록된 jsPDF 인스턴스
 *
 * @example
 * ```typescript
 * const doc = new jsPDF();
 * await registerKoreanFont(doc);
 * doc.setFont('NanumGothic');
 * doc.text('한글 텍스트', 10, 10);
 * ```
 */
export async function registerKoreanFont(doc: jsPDF): Promise<jsPDF> {
  try {
    const fontBase64 = await fetchFontAsBase64();

    // 폰트 파일을 VFS에 추가
    doc.addFileToVFS('NotoSansKR-Regular.ttf', fontBase64);

    // 폰트 등록
    doc.addFont('NotoSansKR-Regular.ttf', KOREAN_FONT_NAME, 'normal');

    // 기본 폰트로 설정
    doc.setFont(KOREAN_FONT_NAME);

    console.log('한글 폰트 등록 완료 (Noto Sans KR)');
    return doc;
  } catch (error) {
    console.error('한글 폰트 등록 실패:', error);
    throw error;
  }
}

/**
 * 폰트 캐시 삭제
 */
export function clearFontCache(): void {
  localStorage.removeItem(CACHE_KEY);
  console.log('한글 폰트 캐시 삭제 완료');
}

/**
 * 폰트가 캐시되어 있는지 확인
 */
export function isFontCached(): boolean {
  return localStorage.getItem(CACHE_KEY) !== null;
}
